import { AudioRecorder, getRecorderErrorMessage } from './audio-recorder';

const MODAL_SELECTOR = '#application-flow-modal';
const FORM_SELECTOR = '#application-form-element';

function smoothScrollToElement(el) {
    if (!el) return;
    if (el.closest(MODAL_SELECTOR)) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
    }
    const rect = el.getBoundingClientRect();
    const top = window.pageYOffset + rect.top - 90;
    window.scrollTo({ top, behavior: 'smooth' });
}

let activeRecorder = null;
let countdownInterval = null;

const audioState = {
    variant: null,
    blob: null,
    filename: null,
    mimeType: null,
    duration: null,
    extension: null,
    objectUrl: null,
};

function clearAudio() {
    if (audioState.objectUrl) {
        URL.revokeObjectURL(audioState.objectUrl);
    }
    audioState.variant = null;
    audioState.blob = null;
    audioState.filename = null;
    audioState.mimeType = null;
    audioState.duration = null;
    audioState.extension = null;
    audioState.objectUrl = null;
    const player = document.querySelector(`${MODAL_SELECTOR} [data-audio-player]`);
    if (player) {
        player.pause();
        player.removeAttribute('src');
        player.load();
    }
}

function isAudioReady() {
    return Boolean(audioState.blob);
}

const ACCEPTED_AUDIO_MIMES = [
    'video/mp4', 'video/webm', 'video/ogg',
    'audio/mp4', 'audio/webm', 'audio/ogg',
];
const MAX_AUDIO_BYTES = 2 * 1024 * 1024;
const MAX_AUDIO_SECONDS = 30;

function uploadExtensionFor(file) {
    const fromName = (file.name || '').toLowerCase().split('.').pop();
    if (['mp4', 'webm', 'ogg', 'm4a'].includes(fromName)) {
        return fromName === 'm4a' ? 'mp4' : fromName;
    }
    const base = (file.type || '').split(';')[0].split('/')[1] || '';
    return base || 'mp4';
}

function probeDurationWith(tagName, url) {
    return new Promise((resolve, reject) => {
        const probe = document.createElement(tagName);
        probe.preload = 'metadata';
        probe.muted = true;
        probe.onloadedmetadata = () => resolve(probe.duration);
        probe.onerror = () => reject(new Error('probe-failed'));
        probe.src = url;
    });
}

function readAudioDuration(file) {
    const url = URL.createObjectURL(file);
    const cleanup = () => URL.revokeObjectURL(url);
    return probeDurationWith('video', url)
        .catch(() => probeDurationWith('audio', url))
        .then((duration) => {
            cleanup();
            return duration;
        }, () => {
            cleanup();
            throw new Error('Nie udało się odczytać pliku audio.');
        });
}

function validateAudioFile(file) {
    if (!ACCEPTED_AUDIO_MIMES.includes(file.type)) {
        return Promise.reject(new Error('Niewspierany format pliku. Dozwolone: MP4, WebM, OGG.'));
    }
    if (file.size > MAX_AUDIO_BYTES) {
        return Promise.reject(new Error('Plik jest za duży. Maksymalny rozmiar to 2 MB.'));
    }
    return readAudioDuration(file).then((duration) => {
        if (!isFinite(duration) || duration <= 0) {
            throw new Error('Nie udało się odczytać długości pliku audio.');
        }
        if (duration > MAX_AUDIO_SECONDS) {
            throw new Error('Nagranie jest dłuższe niż 30 sekund.');
        }
        return duration;
    });
}

function setAudioFromUpload(file, duration) {
    if (audioState.objectUrl) URL.revokeObjectURL(audioState.objectUrl);
    audioState.variant = 'upload';
    audioState.blob = file;
    audioState.mimeType = file.type;
    audioState.extension = uploadExtensionFor(file);
    audioState.duration = duration;
    audioState.filename = file.name || `audio-${Date.now()}.${audioState.extension}`;
    audioState.objectUrl = URL.createObjectURL(file);
}

function getAudioInput() {
    return document.querySelector('[data-audio-file-input]')
        || document.querySelector('input[name="personal_data[files][audio]"]');
}

function triggerFilePicker() {
    const input = getAudioInput();
    if (!input) return;
    input.value = '';
    input.click();
}

function attachAudioToInput() {
    const input = getAudioInput();
    if (!input || !audioState.blob) return false;
    try {
        const file = audioState.blob instanceof File
            ? audioState.blob
            : new File([audioState.blob], audioState.filename, { type: audioState.mimeType });
        const dt = new DataTransfer();
        dt.items.add(file);
        input.files = dt.files;
        return true;
    } catch (e) {
        return false;
    }
}

function showAudioError(message) {
    const $err = $(`${MODAL_SELECTOR} [data-audio-error]`);
    if (message) {
        $err.text(message).removeClass('d-none');
    } else {
        $err.text('').addClass('d-none');
    }
}

function setAudioFromRecording(result) {
    if (audioState.objectUrl) URL.revokeObjectURL(audioState.objectUrl);
    audioState.variant = 'record';
    audioState.blob = result.blob;
    audioState.mimeType = result.mimeType;
    audioState.extension = result.extension;
    audioState.duration = result.duration;
    audioState.filename = `nagranie-${Date.now()}.${result.extension}`;
    audioState.objectUrl = URL.createObjectURL(result.blob);
}

function showShoutError(message) {
    const $err = $(`${MODAL_SELECTOR} [data-shout-error]`);
    if (message) {
        $err.text(message).removeClass('d-none');
    } else {
        $err.text('').addClass('d-none');
    }
}

function showRecordSubview(name) {
    const $step = $(`${MODAL_SELECTOR} [data-step="record-idle"]`);
    $step.find('[data-record-mic-btn]').toggleClass('d-none', name !== 'idle');
    $step.find('[data-record-countdown]').toggleClass('d-none', name !== 'countdown');
    $step.find('[data-record-active]').toggleClass('d-none', name !== 'active');
}

function setRecorderHeader(label, meta) {
    $(`${MODAL_SELECTOR} [data-record-label]`).text(label);
    $(`${MODAL_SELECTOR} [data-record-timer]`).text(meta);
}

function resetRecorderUI() {
    showRecordSubview('idle');
    setRecorderHeader('Nagraj teraz', 'Max 30 sek.');
}

function formatTimer(seconds) {
    const s = Math.max(0, seconds);
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(Math.floor(s) % 60).padStart(2, '0');
    const cs = String(Math.floor((s * 100) % 100)).padStart(2, '0');
    return `${mm}:${ss},${cs}`;
}

function formatTimerShort(seconds) {
    const s = Math.max(0, seconds);
    const mm = String(Math.floor(s / 60)).padStart(2, '0');
    const ss = String(Math.floor(s) % 60).padStart(2, '0');
    return `${mm}:${ss}`;
}

function setPlayerPlaying(isPlaying) {
    $(`${MODAL_SELECTOR} [data-player-icon-play]`).toggleClass('d-none', isPlaying);
    $(`${MODAL_SELECTOR} [data-player-icon-pause]`).toggleClass('d-none', !isPlaying);
    $(`${MODAL_SELECTOR} [data-player-toggle]`).attr('aria-label', isPlaying ? 'Wstrzymaj nagranie' : 'Odtwórz nagranie');
}

function updatePlayerUI(audio) {
    const total = audioState.duration || (isFinite(audio.duration) ? audio.duration : 0);
    const current = audio.currentTime || 0;
    $(`${MODAL_SELECTOR} [data-player-timer]`)
        .text(`${formatTimer(current)} / ${formatTimerShort(total)}`);
    const pct = total > 0 ? Math.min(100, (current / total) * 100) : 0;
    $(`${MODAL_SELECTOR} [data-player-progress-fill]`).css('width', `${pct}%`);
}

function resetPlayerUI() {
    const audio = document.querySelector(`${MODAL_SELECTOR} [data-audio-player]`);
    if (audio) {
        audio.pause();
        try { audio.currentTime = 0; } catch (e) {}
    }
    setPlayerPlaying(false);
    $(`${MODAL_SELECTOR} [data-player-progress-fill]`).css('width', '0%');
    const total = audioState.duration || 0;
    $(`${MODAL_SELECTOR} [data-player-timer]`)
        .text(`${formatTimer(0)} / ${formatTimerShort(total)}`);
}

function bindPlayerEvents(audio) {
    audio.ontimeupdate = () => updatePlayerUI(audio);
    audio.onplay = () => setPlayerPlaying(true);
    audio.onpause = () => setPlayerPlaying(false);
    audio.onended = () => {
        setPlayerPlaying(false);
        try { audio.currentTime = 0; } catch (e) {}
        updatePlayerUI(audio);
    };
    audio.onloadedmetadata = () => {
        if (!isFinite(audio.duration) || audio.duration === Infinity) {
            const fix = () => {
                audio.removeEventListener('timeupdate', fix);
                try { audio.currentTime = 0; } catch (e) {}
                updatePlayerUI(audio);
            };
            audio.addEventListener('timeupdate', fix);
            try { audio.currentTime = 1e101; } catch (e) {}
        }
        updatePlayerUI(audio);
    };
    audio.onerror = () => {
        showAudioError('Nie można odtworzyć nagrania w tej przeglądarce.');
    };
}

$(document).on('click', `${MODAL_SELECTOR} [data-player-toggle]`, function() {
    const audio = document.querySelector(`${MODAL_SELECTOR} [data-audio-player]`);
    if (!audio || !audio.src) return;
    if (audio.paused) {
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
            p.catch((err) => {
                const name = err?.name || '';
                if (name === 'NotSupportedError' || name === 'NotAllowedError') {
                    showAudioError('Nie można odtworzyć nagrania w tej przeglądarce.');
                }
            });
        }
    } else {
        audio.pause();
    }
});

$(document).on('click', `${MODAL_SELECTOR} [data-player-progress]`, function(e) {
    const audio = document.querySelector(`${MODAL_SELECTOR} [data-audio-player]`);
    if (!audio || !audio.src) return;
    const total = audioState.duration || (isFinite(audio.duration) ? audio.duration : 0);
    if (total <= 0) return;
    const rect = this.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    try { audio.currentTime = ratio * total; } catch (e) {}
    updatePlayerUI(audio);
});

function cancelCountdown() {
    if (countdownInterval) {
        clearInterval(countdownInterval);
        countdownInterval = null;
    }
}

function showStep(name) {
    const $modal = $(MODAL_SELECTOR);
    $modal.find('[data-step]').addClass('d-none');
    $modal.find(`[data-step="${name}"]`).removeClass('d-none');
}

function getMainInputs() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return [];
    return Array.from(form.querySelectorAll('input,select,textarea'))
        .filter((el) => !el.disabled && form.contains(el) && !el.matches('[data-audio-file-input]'));
}

function getRuleInputs() {
    return Array.from(document.querySelectorAll(`[form="application-form-element"]`))
        .filter((el) => /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName) && !el.disabled);
}

function validateInputs(inputs) {
    let firstInvalid = null;
    for (const el of inputs) {
        if (!el.checkValidity() && !firstInvalid) firstInvalid = el;
    }
    if (firstInvalid) {
        smoothScrollToElement(firstInvalid);
        firstInvalid.focus({ preventScroll: true });
        return false;
    }
    return true;
}

function clearValidatedState() {
    document.querySelector(FORM_SELECTOR)?.classList.remove('was-validated');
    document.querySelector(MODAL_SELECTOR)?.classList.remove('was-validated');
    hideFlowError();
}

const FIELD_STEP_MAP = {
    rule1: 'rules', rule2: 'rules', rule3: 'rules', rule4: 'rules',
    submit: 'rules',
};

function fieldStep(name) {
    return FIELD_STEP_MAP[name] || null;
}

function isInvalid(el) {
    if (!el.checkValidity()) return true;
    if (el.classList.contains('is-invalid')) return true;
    if (el.classList.contains('has-invalid-feedback-ajax')) return true;
    return false;
}

function baseFieldName(el) {
    const name = el.getAttribute('name') || '';
    return name.replace(/^personal_data\[/, '').replace(/\].*$/, '');
}

function findFirstNonRulesInvalid() {
    const main = getMainInputs().find(isInvalid);
    if (main) return main;
    const nonRulesInModal = getRuleInputs().filter((el) => fieldStep(baseFieldName(el)) !== 'rules');
    return nonRulesInModal.find(isInvalid) || null;
}

function isRoutableErrorKey(form, key) {
    if (!form) return false;
    if (key === form.getAttribute('name') || key === form.name) return false;
    if (!form.elements[key]) return false;
    if (/\[files\]\[audio\]/.test(key)) return false;
    const baseName = key.replace(/^[^[]+\[/, '').replace(/\].*$/, '');
    if (fieldStep(baseName) === 'rules') return false;
    return true;
}

function hasRoutableServerErrors(xhr) {
    const errs = xhr?.responseJSON?.formErrors;
    if (!errs) return false;
    const form = document.querySelector(FORM_SELECTOR);
    return Object.keys(errs).some((key) => isRoutableErrorKey(form, key));
}

function showFlowError() {
    document.querySelector(`${MODAL_SELECTOR} [data-flow-error]`)?.classList.remove('d-none');
}

function hideFlowError() {
    document.querySelector(`${MODAL_SELECTOR} [data-flow-error]`)?.classList.add('d-none');
}

function routeToFirstError() {
    const el = findFirstNonRulesInvalid();
    if (!el) return;
    const targetStep = fieldStep(baseFieldName(el));

    if (targetStep) {
        showStep(targetStep);
        setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus({ preventScroll: true });
        }, 50);
        return;
    }

    $(MODAL_SELECTOR).modal('hide');
    setTimeout(() => {
        smoothScrollToElement(el);
        el.focus({ preventScroll: true });
    }, 250);
}

function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

function renderRegisteredData(data) {
    const fullname = [data.firstname, data.lastname].filter(Boolean).join(' ').trim();
    const rows = [
        ['adres e-mail Uczestnika', data.email],
        ['imię i nazwisko Uczestnika', fullname || null],
        ['numer dowodu dokonania Zakupu Promocyjnego', data.billNr],
        ['datę dokonania Zakupu Promocyjnego', data.billDate],
        ['numer NIP punktu sprzedaży detalicznej,<br>w którym dokonano Zakupu Promocyjnego', data.billNip],
        ['unikalny nr kasy fiskalnej', data.billPos],
    ];
    const html = rows
        .filter(([, val]) => val !== null && val !== undefined && val !== '')
        .map(([label, val]) => `<li>${label}: ${escapeHTML(val)}</li>`)
        .join('');
    document.querySelectorAll(`${MODAL_SELECTOR} [data-registered-list]`).forEach((el) => {
        el.innerHTML = html;
    });
}

$(MODAL_SELECTOR).on('show.bs.modal', function() {
    showShoutError(null);
    showAudioError(null);
    resetRecorderUI();

    if (isAudioReady()) {
        setAudioVariant(audioState.variant);
        showStep('rules');
    } else {
        setAudioVariant(null);
        showStep('shout');
    }
});

$(MODAL_SELECTOR).on('hide.bs.modal', function() {
    cancelCountdown();
    if (activeRecorder) {
        activeRecorder.cancel();
        activeRecorder = null;
    }
    resetRecorderUI();

    const thanksStep = document.querySelector(`${MODAL_SELECTOR} [data-step="thanks"]`);
    if (thanksStep && !thanksStep.classList.contains('d-none')) {
        clearValidatedState();
        clearAudio();
        $('[name="personal_data[billType]"]').trigger('change');
    }
});

function setAudioVariant(variant) {
    const audio = document.querySelector(`${MODAL_SELECTOR} [data-audio-variant]`);
    if (!audio) return;
    const player = audio.querySelector('[data-audio-player]');
    if (!variant) {
        audio.classList.add('d-none');
        audio.removeAttribute('data-audio-variant-active');
        if (player) {
            player.pause();
            player.removeAttribute('src');
            player.load();
        }
        setPlayerPlaying(false);
        return;
    }
    audio.classList.remove('d-none');
    audio.setAttribute('data-audio-variant-active', variant);
    const replayText = audio.querySelector('[data-audio-replay-text]');
    if (replayText) replayText.textContent = variant === 'record' ? 'NAGRAJ PONOWNIE' : 'WGRAJ PONOWNIE';
    const upload = audio.querySelector('[data-audio-icon-upload]');
    const mic = audio.querySelector('[data-audio-icon-mic]');
    if (upload) upload.classList.toggle('d-none', variant !== 'upload');
    if (mic) mic.classList.toggle('d-none', variant !== 'record');
    const playerLabel = audio.querySelector('[data-player-label]');
    if (playerLabel) playerLabel.textContent = variant === 'record' ? 'Dźwięk zarejestrowany' : 'Plik wgrany';
    if (player && audioState.objectUrl) {
        player.pause();
        player.removeAttribute('type');
        player.src = audioState.objectUrl;
        player.load();
        bindPlayerEvents(player);
    }
    resetPlayerUI();
}

$(MODAL_SELECTOR).on('click', '[data-flow-record]', async function() {
    showShoutError(null);

    if (!AudioRecorder.isSupported()) {
        showShoutError('Twoja przeglądarka nie obsługuje nagrywania.');
        return;
    }

    const recorder = new AudioRecorder();
    try {
        await recorder.requestPermission();
    } catch (err) {
        showShoutError(getRecorderErrorMessage(err));
        recorder.cleanup();
        return;
    }

    activeRecorder = recorder;
    resetRecorderUI();
    showStep('record-idle');
});

$(MODAL_SELECTOR).on('click', '[data-flow-upload]', function() {
    showShoutError(null);
    triggerFilePicker();
});

$(document).on('change', '[data-audio-file-input]', async function() {
    const file = this.files && this.files[0];
    if (!file) return;
    const wasInRules = audioState.variant === 'upload' || audioState.variant === 'record';
    try {
        const duration = await validateAudioFile(file);
        setAudioFromUpload(file, duration);
        setAudioVariant('upload');
        showAudioError(null);
        showShoutError(null);
        showStep('rules');
    } catch (err) {
        if (wasInRules) {
            showAudioError(err.message);
        } else {
            showShoutError(err.message);
        }
    } finally {
        this.value = '';
    }
});

$(MODAL_SELECTOR).on('click', '[data-flow-record-start]', function() {
    if (!activeRecorder || !activeRecorder.hasStream()) {
        activeRecorder = null;
        showStep('shout');
        showShoutError('Połączenie z mikrofonem zostało utracone. Spróbuj ponownie.');
        return;
    }
    startCountdown();
});

function startCountdown() {
    showRecordSubview('countdown');
    setRecorderHeader('Za chwilę nagrywanie...', 'Max 30 sek.');
    const $countdown = $(`${MODAL_SELECTOR} [data-record-countdown]`);
    let n = 3;
    $countdown.text(String(n));
    countdownInterval = setInterval(() => {
        n -= 1;
        if (n > 0) {
            $countdown.text(String(n));
        } else {
            cancelCountdown();
            $countdown.text('');
            beginRecording();
        }
    }, 1000);
}

function beginRecording() {
    if (!activeRecorder) {
        showStep('shout');
        return;
    }
    showRecordSubview('active');
    setRecorderHeader('Nagrywanie...', '00:00,00 / 00:30');

    const canvas = document.querySelector(`${MODAL_SELECTOR} [data-record-waveform]`);
    if (canvas) {
        activeRecorder.attachVisualizer(canvas);
    }

    try {
        activeRecorder.start({
            maxSeconds: 29.7,
            onTick: (elapsed) => {
                setRecorderHeader('Nagrywanie...', `${formatTimer(elapsed)} / 00:30`);
            },
            onAutoStop: (result) => {
                handleRecordingResult(result);
            },
            onError: () => {
                activeRecorder = null;
                resetRecorderUI();
                showStep('shout');
                showShoutError('Wystąpił błąd nagrywania. Spróbuj ponownie.');
            },
        });
    } catch (e) {
        activeRecorder = null;
        resetRecorderUI();
        showStep('shout');
        showShoutError('Nie udało się rozpocząć nagrywania.');
    }
}

$(MODAL_SELECTOR).on('click', '[data-flow-record-stop]', async function() {
    if (!activeRecorder) return;
    try {
        const result = await activeRecorder.stop();
        if (result) {
            handleRecordingResult(result);
        } else {
            activeRecorder = null;
            resetRecorderUI();
            showStep('shout');
        }
    } catch (e) {
        activeRecorder = null;
        resetRecorderUI();
        showStep('shout');
        showShoutError('Nie udało się zapisać nagrania. Spróbuj ponownie.');
    }
});

function handleRecordingResult(result) {
    if (!result || !result.blob || result.blob.size === 0) {
        activeRecorder = null;
        resetRecorderUI();
        showStep('shout');
        showShoutError('Nagranie jest puste. Spróbuj ponownie.');
        return;
    }
    setAudioFromRecording(result);
    activeRecorder = null;
    setAudioVariant('record');
    showStep('rules');
    resetRecorderUI();
}

$(MODAL_SELECTOR).on('click', '[data-audio-replay]', async function() {
    const player = document.querySelector(`${MODAL_SELECTOR} [data-audio-player]`);
    if (player && !player.paused) player.pause();
    const audio = document.querySelector(`${MODAL_SELECTOR} [data-audio-variant]`);
    const variant = audio?.getAttribute('data-audio-variant-active');
    if (variant === 'record') {
        showAudioError(null);
        if (!AudioRecorder.isSupported()) {
            showAudioError('Twoja przeglądarka nie obsługuje nagrywania w przeglądarce.');
            return;
        }
        const recorder = new AudioRecorder();
        try {
            await recorder.requestPermission();
        } catch (err) {
            showAudioError(getRecorderErrorMessage(err));
            recorder.cleanup();
            return;
        }
        activeRecorder = recorder;
        showStep('record-idle');
        resetRecorderUI();
    } else if (variant === 'upload') {
        showAudioError(null);
        triggerFilePicker();
    }
});

$(document).on('click', '[data-flow-open]', function() {
    const form = document.querySelector(FORM_SELECTOR);
    form?.classList.add('was-validated');
    const mainInputs = getMainInputs();
    if (!validateInputs(mainInputs)) return;
    const firstServerInvalid = mainInputs.find(isInvalid);
    if (firstServerInvalid) {
        smoothScrollToElement(firstServerInvalid);
        firstServerInvalid.focus({ preventScroll: true });
        return;
    }
    $(MODAL_SELECTOR).modal('show');
});

$(function() {
    const form = document.querySelector(FORM_SELECTOR);
    if (!form) return;
    form.addEventListener('submit', function(e) {
        form.classList.add('was-validated');
        document.querySelector(MODAL_SELECTOR)?.classList.add('was-validated');
        if (!validateInputs([...getMainInputs(), ...getRuleInputs()])) {
            e.preventDefault();
            e.stopImmediatePropagation();
            if (findFirstNonRulesInvalid()) {
                showFlowError();
            } else {
                hideFlowError();
            }
            return;
        }
        if (!isAudioReady()) {
            e.preventDefault();
            e.stopImmediatePropagation();
            showAudioError('Najpierw nagraj okrzyk lub wgraj plik audio.');
            showStep('rules');
            return;
        }
        attachAudioToInput();
        hideFlowError();
    }, true);
});

$('.application-form').on('submit-success', function(event, data, xhr) {
    event.assert(xhr.status == 201 && data.status == 'OK');

    renderRegisteredData(data);
    showStep('thanks');
});

function extractAudioErrorMessage(xhr) {
    const errs = xhr?.responseJSON?.formErrors;
    if (!errs) return null;
    const candidates = [
        errs['personal_data[files][audio]'],
        errs['files[audio]'],
        errs['audio'],
    ];
    for (const v of candidates) {
        if (!v) continue;
        if (typeof v === 'string') return v;
        if (Array.isArray(v) && v.length) return String(v[0]);
        if (typeof v === 'object' && v.message) return String(v.message);
    }
    return null;
}

$('.application-form').on('submit-error', function(event, xhr) {
    document.querySelector(FORM_SELECTOR)?.classList.add('was-validated');
    document.querySelector(MODAL_SELECTOR)?.classList.add('was-validated');

    const audioMsg = extractAudioErrorMessage(xhr);
    if (audioMsg) {
        showAudioError(audioMsg);
        showStep('rules');
    } else {
        showAudioError(null);
    }

    if (xhr?.status >= 400 && xhr.status < 500 && hasRoutableServerErrors(xhr)) {
        showFlowError();
    } else {
        hideFlowError();
    }
});

$(document).on('click', '[data-flow-fix]', function() {
    hideFlowError();
    routeToFirstError();
});

$(MODAL_SELECTOR).on('show.bs.modal', function() {
    hideFlowError();
});

$(MODAL_SELECTOR).on('click', '[data-flow-again]', function() {
    clearValidatedState();
    clearAudio();
    $(MODAL_SELECTOR).modal('hide');
    document.querySelector('#application').scrollIntoView({ behavior: 'smooth', block: 'start' });
});

$('[name="personal_data[billType]"]').on('change', function() {
    var isFaktura = $(this).is(':checked');
    var $group = $('[name="personal_data[billPos]"]').closest('.form-group');
    $group.css({ opacity: isFaktura ? 0.5 : 1, pointerEvents: isFaktura ? 'none' : '' });
    $group.find('input').prop('disabled', isFaktura);
}).trigger('change');
